import { CdkStep, CdkStepper } from '@angular/cdk/stepper';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';

const CIRCLE_BASE =
  '-mb-px flex size-[50px] flex-col items-center rounded-full border-0 px-0 pt-4 ' +
  'text-center text-base leading-none tracking-theme outline-none';
const CIRCLE_ACTIVE = 'cursor-default bg-primary text-white';
const CIRCLE_DONE = 'cursor-pointer bg-[#f5f5f5] text-[#bbb]';
const CIRCLE_PENDING = 'cursor-default bg-[#f5f5f5] text-[#bbb]';

const LABEL_BASE =
  'relative top-[40px] mt-[5px] block text-[14px] leading-[14px] tracking-theme ' + 'whitespace-nowrap';
const LABEL_ACTIVE = 'font-bold text-(--theme-font-color)';
const LABEL_DONE = 'font-normal text-[#bbb]';
const LABEL_PENDING = 'font-normal text-[#ccc]';

/**
 * Step wizard with a circle-per-step header. Steps are `<cdk-step label="...">` children and are all kept alive; only
 * the selected one is shown. Move between steps with `next()`, `previous()` or `selectedIndex`. The user can only go
 * back to steps already visited.
 */
@Component({
  selector: 'app-stepper',
  imports: [NgTemplateOutlet],
  providers: [{ provide: CdkStepper, useExisting: StepperComponent }],
  templateUrl: './stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepperComponent extends CdkStepper {
  readonly #visited = signal<ReadonlySet<number>>(new Set());

  constructor() {
    super();
    this.selectionChange.subscribe(({ previouslySelectedIndex }) =>
      this.#visited.update((visited) => new Set(visited).add(previouslySelectedIndex)),
    );
  }

  protected isDone(index: number): boolean {
    return index !== this.selectedIndex && this.#visited().has(index);
  }

  protected circleClass(index: number): string {
    if (index === this.selectedIndex) return `${CIRCLE_BASE} ${CIRCLE_ACTIVE}`;
    return `${CIRCLE_BASE} ${this.isDone(index) ? CIRCLE_DONE : CIRCLE_PENDING}`;
  }

  protected labelClass(index: number): string {
    if (index === this.selectedIndex) return `${LABEL_BASE} ${LABEL_ACTIVE}`;
    return `${LABEL_BASE} ${this.isDone(index) ? LABEL_DONE : LABEL_PENDING}`;
  }

  protected select(index: number): void {
    if (this.isDone(index)) this.selectedIndex = index;
  }
}

export const STEPPER_IMPORTS = [StepperComponent, CdkStep] as const;
